import { useSelector } from 'react-redux';
import { useEffect, useMemo, useRef } from 'react';
import invariant from 'fbjs/lib/invariant';
import warning from 'fbjs/lib/warning';
import { platformSelector } from '#/store/accessors/device.js';
import renderZones from '#/store/accessors/renderZones.js';

const sides = ['top', 'right', 'bottom', 'left'];

export /**
 *
 * @param {string} zoneId
 * @param {HTMLElement} container
 * @return {{getElementOffset: (function(*=): {offsetTop: *, offsetLeft: *})}}
 * @todo re-use get-element-rect
 */
function useZone(zoneId, container) {
  invariant(zoneId, 'Does not pass required argument `zoneId:string`');

  const platform = useSelector(platformSelector);
  const pixelRatio = useMemo(() => {
    if (platform === 'mobile') {
      return window.devicePixelRatio || 1;
    }
    return 1;
  }, [platform]);
  // const zoneSelector = useMemo(() => accessors.renderZones.createZoneSelector(zoneId), [ zoneId ]);
  const asCSSVars = useMemo(
    () => createAsBorderWidthStyles(pixelRatio),
    [pixelRatio],
  );
  const applyZoneOffset = useRef();

  const { zone, enabled } = useSelector((state) => {
    const zone = renderZones.zoneSelector(state, zoneId);
    warning(zone, 'Could not resolve required zone with id: "%s"', zoneId);

    if (!zone) {
      return {
        zone,
        uniqueKey: '',
      };
    }

    return {
      enabled: [zone.top, zone.right, zone.bottom, zone.left].some(Boolean),
      zone,
      uniqueKey: [zone.top, zone.right, zone.bottom, zone.left].join(),
      // zoneOffsets: `${zone.top}px ${zone.right}px ${zone.bottom}px ${zone.left}px `
    };
  }, styleIsEqual);

  useEffect(() => {
    applyZoneOffset.current = (position) => {
      if (position._dirty) {
        // apply ugly correction due to bound rect does not reflect border width used for safe zone
        position.top -= zone.top / pixelRatio;
        position.left -= zone.left / pixelRatio;
      }

      return position;
    };
  }, [pixelRatio, zone]);
  const zoneStyle = useMemo(() => {
    const styles = sides.map(asCSSVars, zone);
    return styles.reduce(flatKeyValue, {});
  }, [asCSSVars, zone]);

  const getElementPosition = useMemo(() => {
    check(container);
    const applyPixelRatio = (dimension) => {
      dimension.top *= pixelRatio;
      dimension.left *= pixelRatio;
      dimension.width *= pixelRatio;
      dimension.height *= pixelRatio;

      return dimension;
    };

    const implementation = selectImplementation(container);

    return (element) => {
      return implementation(element)
        .then(applyZoneOffset.current)
        .then(applyPixelRatio)
        .then(ceil);
    };
  }, [container, pixelRatio]);

  return {
    enabled,
    zoneStyle,
    getElementPosition,
  };
}

function styleIsEqual({ uniqueKey: prev }, { uniqueKey: next }) {
  return prev === next;
}

function flatKeyValue(acc, [name, value]) {
  return Object.assign({}, acc, { [name]: value });
}

/**
 *
 * @param {number} pixelRatio
 * @return {function(*): string[]}
 */
function createAsBorderWidthStyles(pixelRatio) {
  return asBorderWidthStyles;

  function asBorderWidthStyles(side) {
    // Due to "income" pixels values may be bigger that out webview's internal need to divide to pixel ratio
    const value = Math.ceil((this[side] || 0) / pixelRatio);

    // return [camelizeStyleName(`border-${side}-width`), `${value}px`];
    // TODO: clarify usage, is it possible to simplify flow by setting value in `px`
    return [`--${side}-zone-offset`, `${value}px`];
  }
}

function check(element) {
  warning(
    element === void 0 || element instanceof HTMLElement,
    `Expect HTMLElement argument, ${element} passed.`,
  );
}

/**
 *
 * @param {HTMLElement} element
 * @return {{width: number, height: number}}
 */
function getOffsetSize(element) {
  check(element);
  const { offsetWidth: width, offsetHeight: height } = element;

  return {
    width,
    height,
  };
}

/**
 *
 * @param {HTMLElement} container
 * @return {getOffsets}
 */
function createGetOffset(container) {
  return getOffsets;

  /**
   *
   * @param {HTMLElement} element
   * @return {{top: number, left: number}|*}
   */
  function getOffsets(element) {
    const elementHasTransform = Boolean(
      window.getComputedStyle(element).transform,
    );
    const getOffset = elementHasTransform ? viaBoundRect : viaOffset;
    const { top, left } = getOffset(element);
    const results = {
      top: top,
      left: left,
    };
    const { offsetParent } = element;
    const predicates = [!container, !offsetParent, offsetParent === container];
    const done = predicates.some(Boolean);
    results._dirty = elementHasTransform;

    if (done) {
      return results;
    }

    return sumByKeys(results, getOffsets(offsetParent));
  }

  function viaOffset(element) {
    const { offsetTop: top, offsetLeft: left } = element;

    return {
      top,
      left,
    };
  }

  /**
   * @todo Not optimal way, iterative usage not so funny for performance,
   *    also, bound rect require correction with zone offsets!!!
   * @param element
   * @returns {{top: number, left: number}}
   */
  function viaBoundRect(element) {
    console.log('element has transform');
    const selfRect = element.getBoundingClientRect();
    const parentRect = element.offsetParent.getBoundingClientRect();
    return {
      top: selfRect.top - parentRect.top,
      left: selfRect.left - parentRect.left,
    };
  }
}

// adhoc quick fix
function ceil({ top, left, width, height }) {
  return {
    top: Math.ceil(top),
    left: Math.ceil(left),
    width: Math.ceil(width),
    height: Math.ceil(height),
  };
}

function sumByKeys(r1, r2) {
  return Object.keys(r1).reduce((acc, key) => {
    return {
      ...acc,
      [key]: r1[key] + r2[key],
    };
  }, {});
}

function selectImplementation(container) {
  if (IntersectionObserver !== void 0) {
    return (element) => {
      return new Promise((resolve) => {
        const observer = new IntersectionObserver((entries) => {
          const [entry] = entries;
          let { top, left, width, height } = entry.boundingClientRect;

          observer.disconnect();

          resolve({
            _dirty: true,
            top,
            left,
            width,
            height,
          });
        });
        observer.observe(element);
      });
    };
  }

  const getOffsets = createGetOffset(container);

  return (element) => {
    check(element);
    const { width, height } = getOffsetSize(element);
    const { top, left } = getOffsets(element);
    return Promise.resolve({
      top,
      left,
      width,
      height,
    });
  };
}
